import { createAsyncThunk } from '@reduxjs/toolkit';
import { buildQueryString } from '@/utils/string';
import axiosClient from '@/services/axios';
import {
  getProductDetailsSortedImagesByPriority,
  getProductDetailsSortedImagesByVariant,
} from '@/utils/array';

// Async thunk to fetch products
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (_, { getState, rejectWithValue }) => {
    const { filters, page, limit, selectedFilterItems } = getState().products;
    let queryString = '';

    if (selectedFilterItems?.length > 0) {
      const grouped = selectedFilterItems.reduce((acc, { key, id }) => {
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(id);
        return acc;
      }, {});

      queryString = Object.entries(grouped)
        .map(([key, ids]) => `${key}=${ids.reverse().join(',')}`)
        .join('&');
    }

    // Create a new object while conditionally excluding order_by if it's "default"
    const updatedFilter = {
      ...filters,
      ...(filters.order_by === 'default' ? { order_by: undefined } : {}),
    };

    // Remove `undefined` keys (optional, but recommended)
    Object.keys(updatedFilter).forEach(
      (key) => updatedFilter[key] === undefined && delete updatedFilter[key],
    );

    try {
      const filterObj = {
        ...updatedFilter,
        page,
        limit,
      };

      const url =
        queryString.length > 0
          ? `/api/products${buildQueryString(filterObj)}&${queryString}`
          : `/api/products${buildQueryString(filterObj)}`;
      const result = await axiosClient.get(url);

      return result.data?.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk to fetch additional products
export const fetchInfinityProducts = createAsyncThunk(
  'products/fetchInfinityProducts',
  async (_, { getState, rejectWithValue }) => {
    const { filters, page, limit, selectedFilterItems } = getState().products;
    let queryString = '';

    if (selectedFilterItems?.length > 0) {
      const grouped = selectedFilterItems.reduce((acc, { key, id }) => {
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(id);
        return acc;
      }, {});

      queryString = Object.entries(grouped)
        .map(([key, ids]) => `${key}=${ids.reverse().join(',')}`)
        .join('&');
    }

    // Create a new object while conditionally excluding order_by if it's "default"
    const updatedFilter = {
      ...filters,
      ...(filters.order_by === 'default' ? { order_by: undefined } : {}),
    };

    // Remove `undefined` keys (optional, but recommended)
    Object.keys(updatedFilter).forEach(
      (key) => updatedFilter[key] === undefined && delete updatedFilter[key],
    );

    try {
      const filterObj = {
        ...updatedFilter,
        page,
        limit,
      };

      const url =
        queryString.length > 0
          ? `/api/products${buildQueryString(filterObj)}&${queryString}`
          : `/api/products${buildQueryString(filterObj)}`;
      const result = await axiosClient.get(url);

      return result.data?.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const findMatchingVariant = (allVariantsDetail, searchVariant) => {
  if (!allVariantsDetail || !searchVariant) return { slug: null };

  // Create array of selected attribute combinations
  const selectedCombination = Object.entries(searchVariant).map(
    ([key, value]) => ({
      attribute_name: key,
      attribute_value_id: value.id,
    }),
  );

  // Find variant with matching element
  const data = allVariantsDetail.find((variant) => {
    const variantCombination = variant?.elements.map((combo) => ({
      attribute_name: combo.attribute_name?.en,
      attribute_value_id: combo.attribute_value_id,
    }));

    // Check if all attributes match
    return selectedCombination.every((selected) =>
      variantCombination.some(
        (v) =>
          // TODO: hide this because name can be change
          // v.attribute_name === selected.attribute_name &&
          v.attribute_value_id === selected.attribute_value_id,
      ),
    );
  });

  return data;
};

export function calculateRatingSummary(state) {
  const totalRatings = state.items.length;
  const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

  let totalScore = 0;
  const totalRatingValues = 0;
  state.items.forEach((item) => {
    const { rating } = item;
    if (rating >= 1 && rating <= 5) {
      distribution[rating] += 1;
      totalScore += rating;
    }
  });

  const average = totalRatings ? (totalScore / totalRatings).toFixed(1) : 0;

  state.ratingSummary = {
    average: parseFloat(average),
    totalRatings,
    distribution,
  };
}

export const getActiveVariantDetail = ({
  productDetail,
  initialVariant = null,
}) => {
  try {
    // Check if product exists and has variants
    if (!productDetail?.product) {
      return null;
    }

    const { product, variants = [] } = productDetail;

    let activeVariant = null;

    if (Array.isArray(variants) && variants?.length > 0) {
      let defaultVariant = null;

      if (initialVariant) {
        defaultVariant = variants.find(
          (variant) => variant.slug === initialVariant,
        );
      }

      // if initial variant not found in variants
      if (!defaultVariant) {
        // Find default variant or variant with minimum regular_price
        defaultVariant = variants.find((variant) => variant.is_default);
      }

      const selectedVariant =
        defaultVariant ||
        variants.reduce(
          (minVariant, currentVariant) =>
            currentVariant.regular_price < minVariant.regular_price
              ? currentVariant
              : minVariant,
          variants[0],
        );

      activeVariant = selectedVariant
        ? {
            ...selectedVariant,
            parentSlug: product?.slug,
            parentName: product?.name,
            image_1x1: product?.image_1x1 ?? null,
            details_page_image:
              getProductDetailsSortedImagesByVariant(
                product?.images?.tall,
                selectedVariant.slug,
              ) ?? [],
            image: product?.image,
            ribbon: product?.ribbon,
            brand: product.brand,
            stock_buffer: product.stock_buffer,
          }
        : null;
    } else {
      // Set product as active variant if no variant found
      activeVariant = {
        ...product,
        parentSlug: null,
        parentName: null,
        image_1x1: product?.image_1x1 ?? null,
        details_page_image:
          getProductDetailsSortedImagesByPriority(product?.images?.tall) ?? [],
        image: product?.image,
        ribbon: product?.ribbon,
        brand: product.brand,
        stock_buffer: product.stock_buffer,
      };
    }

    return activeVariant;
  } catch (error) {
    console.error('Error setting active variant:', error);
    return null;
  }
};

export const getFilterKey = createAsyncThunk(
  'products/fetchFilters',
  async (
    { parentCategory, type, brandSlug, searchKey, campaignSlug },
    { rejectWithValue },
  ) => {
    try {
      let url;

      if (type === 'category') {
        url = `/api/category/${parentCategory}`;
      } else if (type === 'campaign') {
        // TODO: NEEDS TO UPDATE
        url = `/api/campaign-filter/${campaignSlug}`;
      } else if (type === 'brand') {
        url = `/api/brand/${brandSlug}`;
      }

      const result = await axiosClient.get(url);

      return result.data?.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);
